import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import {
  saveSermonOffline,
  loadSermonOffline,
  processSyncQueue
} from '../lib/offlineStorage';
import type { UserPermission } from '../types/teams';

// Upper bound on stored sermon content. Set well above any realistic sermon
// (largest in production is ~57k chars) so it only ever catches accidental
// multi-MB pastes or abuse, never legitimate content.
const MAX_SERMON_CONTENT_LENGTH = 500_000;

interface Sermon {
  id: string;
  user_id?: string;
  title: string;
  scripture: string;
  content: string;
  date: string | null;
  status: 'draft' | 'complete';
  series_id: string | null;
  youtube_url: string | null;
  version?: number;
}

interface AutosaveConfig {
  enabled: boolean;
  interval: number;
}

export function useSermon(sermonId?: string) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [currentVersion, setCurrentVersion] = useState<number>(0);
  const [userPermission, setUserPermission] = useState<UserPermission | null>(null);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);
  const [offlineMode, setOfflineMode] = useState<boolean>(!navigator.onLine);
  const [autosaveConfig, setAutosaveConfig] = useState<AutosaveConfig>(() => {
    const saved = localStorage.getItem('sermon_autosave_config');
    return saved ? JSON.parse(saved) : {
      enabled: true,
      interval: 15000 // 15 seconds default
    };
  });

  const contentRef = useRef<string>('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveAttemptRef = useRef<number>(0);
  const isAutosaveRef = useRef<boolean>(false);
  const pendingSaveRef = useRef<boolean>(false);
  const lastContentRef = useRef<string>('');
  const saveInProgressRef = useRef<boolean>(false);
  const lastAutosaveTimeRef = useRef<number>(Date.now());
  const hasUnsavedChangesRef = useRef<boolean>(false);
  const sermonDataRef = useRef<Partial<Sermon>>({});
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentSermonIdRef = useRef<string | undefined>(sermonId);

  // Update currentSermonIdRef when sermonId changes
  useEffect(() => {
    currentSermonIdRef.current = sermonId;
  }, [sermonId]);

  // Save autosave config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sermon_autosave_config', JSON.stringify(autosaveConfig));
  }, [autosaveConfig]);

  // Set up online/offline listeners
  useEffect(() => {
    const handleOnline = () => {
      console.log('Connection restored');
      setIsOffline(false);
      // Try to sync when we come back online
      processSyncQueue();
    };
    
    const handleOffline = () => {
      console.log('Connection lost');
      setIsOffline(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Set up beforeunload handler to warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        // Save changes before unloading
        if (contentRef.current && !saveInProgressRef.current) {
          const sermonData = {
            ...sermonDataRef.current,
            content: contentRef.current
          };
          
          // Use a synchronous approach for beforeunload
          saveInProgressRef.current = true;
          
          // For offline mode, we can use localStorage as fallback
          if (isOffline || offlineMode) {
            try {
              // We can't await in beforeunload, so we use the synchronous localStorage as fallback
              localStorage.setItem(`emergency_save_${currentSermonIdRef.current || 'new'}`, JSON.stringify({
                content: contentRef.current,
                timestamp: Date.now()
              }));
            } catch (err) {
              console.error('Emergency save failed:', err);
            }
          }
          
          // Standard beforeunload message
          e.preventDefault();
          e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
          return e.returnValue;
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sermonId, isOffline, offlineMode]);

  const updateAutosaveConfig = useCallback((newConfig: Partial<AutosaveConfig>) => {
    setAutosaveConfig(prev => {
      const updated = { ...prev, ...newConfig };
      localStorage.setItem('sermon_autosave_config', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const saveSermon = useCallback(async (data: Partial<Sermon>) => {
    console.log('saveSermon called with data:', Object.keys(data));

    // Guardrail against pathological input (e.g. an accidental multi-MB paste).
    // The cap is far above any real sermon — the largest in production is ~57k
    // chars — so this never affects legitimate content, only runaway input.
    if (typeof data.content === 'string' && data.content.length > MAX_SERMON_CONTENT_LENGTH) {
      console.warn('Sermon content exceeds max length, rejecting save:', data.content.length);
      setError('This sermon is too large to save. Please shorten it and try again.');
      return null;
    }

    // Update the sermon data ref
    sermonDataRef.current = {
      ...sermonDataRef.current,
      ...data
    };
    
    // Don't save if content hasn't changed and this is an autosave
    if (isAutosaveRef.current && data.content && data.content === lastContentRef.current) {
      console.log('Content unchanged, skipping autosave');
      isAutosaveRef.current = false;
      return null;
    }
    
    // Update last content reference
    if (data.content) {
      lastContentRef.current = data.content;
    }
    
    // Prevent concurrent saves
    if (saveInProgressRef.current) {
      console.log('Save already in progress, queueing for later');
      pendingSaveRef.current = true;
      hasUnsavedChangesRef.current = true;
      return null;
    }
    
    try {
      saveInProgressRef.current = true;
      setSaving(true);
      setError(null);

      // Check if we're online or offline
      const isCurrentlyOffline = !navigator.onLine;
      
      // If we're offline or were already in offline mode, save locally
      if (isCurrentlyOffline || offlineMode) {
        console.log('Saving in offline mode');
        setOfflineMode(true);
        
        // Sermon date is a calendar date ('yyyy-MM-dd'), not an instant —
        // converting to a UTC timestamp shifts the stored day for users east of UTC.
        const dateValue = data.date ? data.date.slice(0, 10) : null;

        // If this is a new sermon, create it with a UUID
        const sermonUuid = currentSermonIdRef.current || uuidv4();
        
        // Save to local storage
        const offlineSermon = await saveSermonOffline({
          id: sermonUuid,
          title: data.title || sermonDataRef.current.title || '',
          scripture: data.scripture || sermonDataRef.current.scripture || '',
          content: data.content || sermonDataRef.current.content || '',
          status: data.status || sermonDataRef.current.status || 'draft',
          series_id: data.series_id || sermonDataRef.current.series_id || null,
          date: dateValue || sermonDataRef.current.date || null,
          youtube_url: data.youtube_url || sermonDataRef.current.youtube_url || null,
          version: currentVersion + 1,
          lastModified: Date.now(),
          pendingSync: true
        });
        
        setLastSaved(new Date());
        setCurrentVersion(offlineSermon.version);
        hasUnsavedChangesRef.current = false;
        
        // If we're online, try to sync
        if (navigator.onLine) {
          processSyncQueue();
        }
        
        return {
          id: sermonUuid,
          ...data,
          version: offlineSermon.version
        };
      }

      // If we're online, proceed with normal save to Supabase
      console.log('Saving to Supabase');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Sermon date is a calendar date ('yyyy-MM-dd'), not an instant —
      // converting to a UTC timestamp shifts the stored day for users east of UTC.
      const dateValue = data.date ? data.date.slice(0, 10) : null;

      // If this is a new sermon, create it
      if (!currentSermonIdRef.current) {
        console.log('Creating new sermon');
        const { error: insertError, data: newSermon } = await supabase
          .from('sermons')
          .insert([{
            ...data,
            date: dateValue,
            user_id: user.id,
            status: data.status || 'draft',
            updated_at: new Date().toISOString(),
            version: 1 // Initial version
          }])
          .select()
          .single();

        if (insertError) throw insertError;
        console.log('New sermon created:', newSermon.id);
        
        // Update the current sermon ID reference
        currentSermonIdRef.current = newSermon.id;
        
        setLastSaved(new Date());
        setCurrentVersion(1);
        hasUnsavedChangesRef.current = false;
        
        // Save to offline storage for future offline access
        await saveSermonOffline({
          id: newSermon.id,
          title: newSermon.title,
          scripture: newSermon.scripture,
          content: newSermon.content,
          status: newSermon.status,
          series_id: newSermon.series_id,
          date: newSermon.date,
          youtube_url: newSermon.youtube_url,
          version: 1,
          lastModified: Date.now(),
          pendingSync: false
        });
        
        return newSermon;
      } else {
        console.log('Updating existing sermon:', currentSermonIdRef.current);
        // First, get the current version from the database
        const { data: currentSermon, error: fetchError } = await supabase
          .from('sermons')
          .select('version, date, content, youtube_url')
          .eq('id', currentSermonIdRef.current)
          .single();

        if (fetchError) throw fetchError;
        
        const serverVersion = currentSermon?.version || 0;
        console.log('Server version:', serverVersion, 'Local version:', currentVersion);
        
        // Only update if our version is current or higher, or if this is an autosave
        if (currentVersion >= serverVersion || isAutosaveRef.current) {
          // Update existing sermon with incremented version.
          // NOTE: We do NOT filter by user_id here — the RLS policy
          // "Owners and team editors can update sermons" enforces write
          // access at the DB level. Editors (team permission = 'editor')
          // are allowed to save. Last-write-wins in v1; no conflict resolution.
          console.log('Updating sermon with new version:', serverVersion + 1);
          const { error: updateError, data: updatedSermon } = await supabase
            .from('sermons')
            .update({
              ...data,
              date: data.date !== undefined ? dateValue : currentSermon.date,
              updated_at: new Date().toISOString(),
              version: serverVersion + 1
            })
            .eq('id', currentSermonIdRef.current)
            .select()
            .single();

          if (updateError) throw updateError;
          console.log('Sermon updated successfully');
          setLastSaved(new Date());
          setCurrentVersion(serverVersion + 1);
          hasUnsavedChangesRef.current = false;
          
          // Save to offline storage for future offline access
          await saveSermonOffline({
            id: updatedSermon.id,
            title: updatedSermon.title,
            scripture: updatedSermon.scripture,
            content: updatedSermon.content,
            status: updatedSermon.status,
            series_id: updatedSermon.series_id,
            date: updatedSermon.date,
            youtube_url: updatedSermon.youtube_url,
            version: updatedSermon.version,
            lastModified: Date.now(),
            pendingSync: false
          });
          
          // Only show version conflict if it's not an autosave
          if (serverVersion > currentVersion && !isAutosaveRef.current) {
            setError('Another device has made changes to this sermon. The latest version has been loaded.');
          }
          
          // Reset autosave flag
          isAutosaveRef.current = false;
          
          return updatedSermon;
        } else {
          // Our version is behind, fetch the latest content
          console.log('Local version behind, fetching latest');
          const { data: latestSermon, error: latestError } = await supabase
            .from('sermons')
            .select('*')
            .eq('id', currentSermonIdRef.current)
            .single();

          if (latestError) throw latestError;
          
          // Update our local version
          setCurrentVersion(latestSermon.version);
          
          // If we're trying to save content, but server has newer content
          if (data.content) {
            setError('Another device has made changes to this sermon. The latest version has been loaded.');
            // Update our content ref with the latest content
            contentRef.current = latestSermon.content;
          }
          
          setLastSaved(new Date(latestSermon.updated_at));
          hasUnsavedChangesRef.current = false;
          
          // Save to offline storage for future offline access
          await saveSermonOffline({
            id: latestSermon.id,
            title: latestSermon.title,
            scripture: latestSermon.scripture,
            content: latestSermon.content,
            status: latestSermon.status,
            series_id: latestSermon.series_id,
            date: latestSermon.date,
            youtube_url: latestSermon.youtube_url,
            version: latestSermon.version,
            lastModified: Date.now(),
            pendingSync: false
          });
          
          return latestSermon;
        }
      }
    } catch (e) {
      console.error('Error saving sermon:', e);
      
      // If we're having connection issues, switch to offline mode
      if (e instanceof Error && 
          (e.message.includes('Failed to fetch') || 
           e.message.includes('NetworkError') || 
           e.message.includes('network') ||
           e.message.includes('connection'))) {
        
        setOfflineMode(true);
        setError('You appear to be offline. Your changes will be saved locally and synced when your connection is restored.');
        
        // Try to save offline
        try {
          const sermonUuid = currentSermonIdRef.current || uuidv4();
          
          // Sermon date is a calendar date ('yyyy-MM-dd'), not an instant —
          // converting to a UTC timestamp shifts the stored day for users east of UTC.
          const dateValue = data.date ? data.date.slice(0, 10) : null;
          
          const offlineSermon = await saveSermonOffline({
            id: sermonUuid,
            title: data.title || sermonDataRef.current.title || '',
            scripture: data.scripture || sermonDataRef.current.scripture || '',
            content: data.content || sermonDataRef.current.content || '',
            status: data.status || sermonDataRef.current.status || 'draft',
            series_id: data.series_id || sermonDataRef.current.series_id || null,
            date: dateValue || sermonDataRef.current.date || null,
            youtube_url: data.youtube_url || sermonDataRef.current.youtube_url || null,
            version: currentVersion + 1,
            lastModified: Date.now(),
            pendingSync: true
          });
          
          setLastSaved(new Date());
          setCurrentVersion(offlineSermon.version);
          hasUnsavedChangesRef.current = false;
          
          return {
            id: sermonUuid,
            ...data,
            version: offlineSermon.version
          };
        } catch (offlineError) {
          console.error('Error saving sermon offline:', offlineError);
          setError('Failed to save sermon. Please try again.');
          return null;
        }
      } else {
        setError(e instanceof Error ? e.message : 'Failed to save sermon');
        return null;
      }
    } finally {
      setSaving(false);
      saveInProgressRef.current = false;
      
      // If there's a pending save, trigger it after a short delay
      if (pendingSaveRef.current && contentRef.current) {
        setTimeout(() => {
          if (pendingSaveRef.current) {
            isAutosaveRef.current = true;
            pendingSaveRef.current = false;
            saveSermon({ content: contentRef.current });
          }
        }, 1000);
      }
    }
  }, [currentVersion, offlineMode]);

  const loadSermon = useCallback(async (id: string) => {
    try {
      setError(null);
      
      // Update the current sermon ID reference
      currentSermonIdRef.current = id;
      
      // Check if we're online or offline
      const isCurrentlyOffline = !navigator.onLine;
      
      // If we're offline, try to load from local storage
      if (isCurrentlyOffline || offlineMode) {
        setOfflineMode(true);
        
        const offlineSermon = await loadSermonOffline(id);
        
        if (offlineSermon) {
          contentRef.current = offlineSermon.content;
          lastContentRef.current = offlineSermon.content;
          setCurrentVersion(offlineSermon.version);
          setLastSaved(new Date(offlineSermon.lastModified));
          
          // Update sermon data ref
          sermonDataRef.current = {
            id: offlineSermon.id,
            title: offlineSermon.title,
            scripture: offlineSermon.scripture,
            content: offlineSermon.content,
            status: offlineSermon.status,
            series_id: offlineSermon.series_id,
            date: offlineSermon.date,
            youtube_url: offlineSermon.youtube_url,
            version: offlineSermon.version
          };
          
          return {
            id: offlineSermon.id,
            title: offlineSermon.title,
            scripture: offlineSermon.scripture,
            content: offlineSermon.content,
            status: offlineSermon.status,
            series_id: offlineSermon.series_id,
            date: offlineSermon.date,
            youtube_url: offlineSermon.youtube_url,
            version: offlineSermon.version
          };
        } else {
          setError('Sermon not found in offline storage');
          return null;
        }
      }
      
      // If we're online, try to load from Supabase
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // No user_id filter — RLS SELECT policy handles access for both
      // owners and team members. The userPermission state is set below.
      const { data, error } = await supabase
        .from('sermons')
        .select(`
          *,
          series:series_id (
            id,
            title,
            description
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        // If we get a network error, try offline
        if (error.message.includes('Failed to fetch') || 
            error.message.includes('NetworkError') || 
            error.message.includes('network') ||
            error.message.includes('connection')) {
          
          setOfflineMode(true);
          
          const offlineSermon = await loadSermonOffline(id);
          
          if (offlineSermon) {
            contentRef.current = offlineSermon.content;
            lastContentRef.current = offlineSermon.content;
            setCurrentVersion(offlineSermon.version);
            setLastSaved(new Date(offlineSermon.lastModified));
            
            // Update sermon data ref
            sermonDataRef.current = {
              id: offlineSermon.id,
              title: offlineSermon.title,
              scripture: offlineSermon.scripture,
              content: offlineSermon.content,
              status: offlineSermon.status,
              series_id: offlineSermon.series_id,
              date: offlineSermon.date,
              youtube_url: offlineSermon.youtube_url,
              version: offlineSermon.version
            };
            
            return {
              id: offlineSermon.id,
              title: offlineSermon.title,
              scripture: offlineSermon.scripture,
              content: offlineSermon.content,
              status: offlineSermon.status,
              series_id: offlineSermon.series_id,
              date: offlineSermon.date,
              youtube_url: offlineSermon.youtube_url,
              version: offlineSermon.version
            };
          } else {
            throw new Error('Sermon not found in offline storage');
          }
        } else {
          throw error;
        }
      }
      
      // Save to offline storage for future offline access
      await saveSermonOffline({
        id: data.id,
        title: data.title,
        scripture: data.scripture,
        content: data.content,
        status: data.status,
        series_id: data.series_id,
        date: data.date,
        youtube_url: data.youtube_url,
        version: data.version || 1,
        lastModified: Date.now(),
        pendingSync: false
      });
      
      if (data?.content) {
        contentRef.current = data.content;
        lastContentRef.current = data.content;
      } else {
        contentRef.current = '';
        lastContentRef.current = '';
      }
      if (data?.version) {
        setCurrentVersion(data.version);
      } else {
        setCurrentVersion(1);
      }
      if (data?.updated_at) {
        setLastSaved(new Date(data.updated_at));
      }

      // Resolve effective permission for the current user
      if (data?.user_id === user.id) {
        setUserPermission('owner');
      } else {
        // Find which teams this user is an accepted member of
        const { data: memberRows } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('user_id', user.id)
          .eq('status', 'accepted');

        const teamIds = (memberRows || []).map((m: any) => m.team_id);

        if (teamIds.length > 0) {
          const { data: shareData } = await supabase
            .from('team_sermon_shares')
            .select('permission')
            .eq('sermon_id', id)
            .in('team_id', teamIds);

          if (shareData && shareData.length > 0) {
            const isEditor = shareData.some((s: any) => s.permission === 'editor');
            setUserPermission(isEditor ? 'editor' : 'viewer');
          } else {
            setUserPermission('viewer');
          }
        } else {
          setUserPermission('viewer');
        }
      }
      
      // Update sermon data ref
      sermonDataRef.current = {
        id: data.id,
        title: data.title,
        scripture: data.scripture,
        content: data.content,
        status: data.status,
        series_id: data.series_id,
        date: data.date,
        youtube_url: data.youtube_url,
        version: data.version
      };
      
      return data;
    } catch (e) {
      console.error('Error loading sermon:', e);
      setError(e instanceof Error ? e.message : 'Failed to load sermon');
      return null;
    }
  }, [offlineMode]);

  // Function to manually trigger autosave
  const triggerAutosave = useCallback(() => {
    if (autosaveConfig.enabled && contentRef.current && !saveInProgressRef.current) {
      console.log('Manually triggering autosave');
      isAutosaveRef.current = true;
      lastAutosaveTimeRef.current = Date.now();
      
      // Get the latest sermon data
      const sermonData = {
        ...sermonDataRef.current,
        content: contentRef.current
      };
      
      // Save the sermon
      saveSermon(sermonData);
      hasUnsavedChangesRef.current = false;
    }
  }, [autosaveConfig.enabled, saveSermon]);

  // Function to save before navigating away
  const saveBeforeExit = useCallback(async () => {
    if (hasUnsavedChangesRef.current && !saveInProgressRef.current) {
      console.log('Saving before exit');
      
      // Get the latest sermon data
      const sermonData = {
        ...sermonDataRef.current,
        content: contentRef.current
      };
      
      // Save the sermon
      await saveSermon(sermonData);
      return true;
    }
    return false;
  }, [saveSermon]);

  // Set up content change handler
  const handleContentChange = useCallback((content: string) => {
    // Store the content
    contentRef.current = content;
    
    // Mark as having unsaved changes
    hasUnsavedChangesRef.current = true;
    
    // Clear any existing autosave timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }
    
    // Set up autosave timeout if enabled
    if (autosaveConfig.enabled) {
      autosaveTimeoutRef.current = setTimeout(() => {
        console.log('Autosave triggered by content change');
        isAutosaveRef.current = true;
        
        // Save the sermon
        saveSermon({
          ...sermonDataRef.current,
          content
        });
        
        // Reset the timeout ref
        autosaveTimeoutRef.current = null;
      }, autosaveConfig.interval);
    }
  }, [autosaveConfig.enabled, autosaveConfig.interval, saveSermon]);

  return {
    saving,
    error,
    lastSaved,
    currentVersion,
    autosaveConfig,
    isOffline,
    offlineMode,
    userPermission,
    updateAutosaveConfig,
    saveSermon,
    loadSermon,
    triggerAutosave,
    saveBeforeExit,
    setContent: handleContentChange
  };
}